import { Camp67CompactHeader } from '../components/camp67-header/camp67-header';
import { MetaFunction } from '@remix-run/cloudflare';
import { useNavigate, useSearchParams } from '@remix-run/react';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { parseDate, bathingDates } from '@camp67/model/booking/dates';
import { TentTypeV1 } from '@camp67/model';
import { MobileNavigation } from '../components/camp67-header/navigation';
import { images } from '../constants/images';
import clsx from 'clsx';
import { isInquiryModeEnabled, onOpenInquiry } from '../state/inquiry-modal';
import {
  BookingForm,
  BookingFormV4,
  BookingTimeoutDialog,
  reservationStore,
  packagesState,
} from '@kumbh/booking-components';
import {
  useTentConfigs,
  useTentConfigsV4,
  useBookingFormMessages,
  useBookingFormV4Messages,
  bookingFormRoutes,
  useRenderIcon,
} from '../hooks/use-booking-form-config';
import { useMessages } from '../i18n/useMessages';

export const meta: MetaFunction = () => {
  return [
    { title: 'Book your trip - camp67' },
    {
      name: 'description',
      content:
        'Book now for Magh Mela 2026. We offer multiple kinds of tents for travellers, families, and groups.',
    },
    {
      tagName: 'link',
      name: 'canonical',
      href: 'https://camp67.com/reservation',
    },
  ];
};

/**
 * Configurable time limit for a booking in ms. When it ends,
 * the booking form will be cleared and reset.
 *
 * Currently: 30 minutes.
 */
const BOOKING_TIMEOUT_LIMIT = 30 * 60 * 1000;

export default function Reservation() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const getMessage = useMessages();

  // Feature flag: use ?v=3 to force legacy V3 booking form
  const useLegacyV3 = searchParams.get('v') === '3';

  // Get tent configs and messages for the shared BookingForm (V3 - legacy)
  const tentConfigs = useTentConfigs();
  const bookingMessages = useBookingFormMessages();
  const renderIcon = useRenderIcon();

  // Get tent configs and messages for V4 BookingForm
  const tentConfigsV4 = useTentConfigsV4();
  const bookingMessagesV4 = useBookingFormV4Messages();

  // Get packages for V4
  const packages = useMemo(() => packagesState.packages, []);

  // Redirect to home and open inquiry modal if in inquiry mode
  useEffect(() => {
    if (isInquiryModeEnabled) {
      onOpenInquiry();
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Initialize from URL params
  useEffect(() => {
    if (isInquiryModeEnabled) return; // Skip initialization if redirecting

    const from = searchParams.get('from') as string | null;
    const to = searchParams.get('to') as string | null;

    const type = searchParams.get('tentType') as string | null;

    if (type !== null) {
      const typeDecoded = decodeURIComponent(type);

      reservationStore.clearRooms();
      reservationStore.addRoom(typeDecoded as TentTypeV1);
    }

    const startDate = from ? parseDate(from) : undefined;
    const endDate = to ? parseDate(to) : undefined;

    if (startDate && endDate) {
      reservationStore.chooseDateStart(startDate);
      reservationStore.chooseDateRange(endDate);
    }
  }, [searchParams, setSearchParams]);

  const [isTimerWarningOpen, setIsTimerWarningOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsTimerWarningOpen(true);
    }, BOOKING_TIMEOUT_LIMIT);

    return () => clearTimeout(timeout);
  }, []);

  const onCloseTimeout = useCallback(() => {
    setIsTimerWarningOpen(false);
  }, []);

  const onNavigateToPayment = useCallback(() => {
    navigate(bookingFormRoutes.completePayment);
  }, [navigate]);

  return (
    <div className="bg-yellow-50 min-h-screen">
      <BookingTimeoutDialog
        isOpen={isTimerWarningOpen}
        onClose={onCloseTimeout}
        messages={{
          title: getMessage('camp67-reservation.timeout.title'),
          description: getMessage('camp67-reservation.timeout.description'),
          okButton: getMessage('camp67-reservation.timeout.ok'),
        }}
      />
      <div className="md:hidden flex px-4 justify-between items-center py-2">
        <img
          loading="lazy"
          alt="The logo of camp67, a Kumbh campsite in Prayagraj, India."
          src={images['logo-dark']}
          className={clsx('select-none pointer-events-none w-10')}
        />
        <h1 className="font-serif font-extrabold text-lg">Book a room</h1>
        <MobileNavigation textColor="dark" headerAppearance="subtle" size="compact" />
      </div>
      <div className="m-auto hidden sm:block w-full xl:px-60 lg:px-24 md:px-16 px-8 rounded-b-md border-dotted border-b-black border-b ">
        <Camp67CompactHeader />
      </div>
      <main className="w-full h-full sm:first-line:my-8">
        <section
          id="camp67-booking-experience"
          className="w-full h-full xl:px-60 lg:px-24 md:px-16 px-0 overflow-y-auto"
        >
          {useLegacyV3 ? (
            <BookingForm
              brand="camp67"
              tents={tentConfigs}
              messages={bookingMessages}
              routes={bookingFormRoutes}
              logoUrl={images['logo-dark']}
              logoAlt="camp67 logo"
              onNavigateToPayment={onNavigateToPayment}
              renderIcon={renderIcon}
            />
          ) : (
            <BookingFormV4
              formId="camp67-booking-form-v4"
              tents={tentConfigsV4}
              packages={packages}
              messages={bookingMessagesV4}
              onNavigateToPayment={onNavigateToPayment}
              minDate={reservationStore.getStartOfBookingTimeRange()}
              maxDate={reservationStore.getEndOfBookingTimeRange()}
              bathingDates={bathingDates}
            />
          )}
        </section>
      </main>
    </div>
  );
}
